'use client';

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import { useRouter } from '@/i18n/routing';
import {
  ScanLine,
  Circle,
  AlertCircle,
  AlertTriangle,
  TrendingUp,
  FileText,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

import { cn } from '@/lib/utils';
import { API_ENDPOINTS } from '@/lib/api/config';
import { useDataTable } from '@/hooks/use-data-table';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { RouteGate } from '@/components/route-gate';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { PermissionGate } from '@/features/auth';
import {
  MoveToRepairDialog,
  type MoveToRepairPayload
} from '@/features/repair-settings';
import { photoScanningQueries } from '../services/photo-scanning.queries';
import {
  useStartBatchMutation,
  useRefuseBatchMutation
} from '../services/photo-scanning.mutations';
import { devicesApi, deviceKeys, type Device } from '@/features/devices';
import { usersApi, userKeys, type User } from '@/features/users';
import { PHOTO_SCANNING_HASH } from '../constants';
import { PhotoScanningTableActionBar } from './photo-scanning-table-action-bar';
import { PhotoScanningCard } from './photo-scanning-card';
import { PhotoScanningCompactRow } from './photo-scanning-compact-row';
import { BatchPdfViewer } from './batch-pdf-viewer';
import {
  RepairHistoryButton,
  REPAIR_STATUS_STYLES
} from './repair-history-button';
import type {
  BatchStatus,
  RepairCycleStatus,
  ScanBatch,
  ScanBatchListParams
} from '../types';

// ─── Status indicators that refresh every 30 s ───────────────────────────────
function SystemStatusBar() {
  const t = useTranslations('photoScanning');
  const [systemOnline, setSystemOnline] = useState(true);
  const [dbSynced, setDbSynced] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      // In production: poll real status endpoint
      setSystemOnline(true);
      setDbSynced(true);
    }, 30_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className='flex items-center gap-4 text-sm'>
      <span className='flex items-center gap-1.5'>
        <Circle
          className={cn(
            'h-2 w-2 fill-current',
            systemOnline ? 'text-emerald-500' : 'text-rose-500'
          )}
        />
        <span
          className={cn(
            systemOnline
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-rose-600'
          )}
        >
          {t('systemOnline')}
        </span>
      </span>
      <span className='flex items-center gap-1.5'>
        <Circle
          className={cn(
            'h-2 w-2 fill-current',
            dbSynced ? 'text-emerald-500' : 'text-rose-500'
          )}
        />
        <span
          className={cn(
            dbSynced
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-rose-600'
          )}
        >
          {t('dbSynced')}
        </span>
      </span>
      <span className='text-muted-foreground text-xs'>{t('lastUpdated')}</span>
    </div>
  );
}

// ─── Metrics card ─────────────────────────────────────────────────────────────
function TotalMetricsCard() {
  const t = useTranslations('photoScanning');
  const { data } = useQuery(photoScanningQueries.metrics());

  const metrics = data?.data;
  const total = metrics?.total ?? 0;
  const trend = metrics?.trend_percent ?? 0;

  return (
    <div className='flex flex-col items-end'>
      <span className='text-muted-foreground text-xs'>{t('totalCounter')}</span>
      <div className='flex items-baseline gap-2'>
        <span className='text-3xl font-bold tracking-tight'>
          {total.toLocaleString()}
        </span>
        <span className='flex items-center gap-0.5 text-sm font-medium text-emerald-600'>
          <TrendingUp className='h-3.5 w-3.5' />
          {trend > 0 ? '+' : ''}
          {trend}%
        </span>
      </div>
    </div>
  );
}

// ─── Main listing component ───────────────────────────────────────────────────
export function PhotoScanningListing() {
  const t = useTranslations('photoScanning');
  const tRepair = useTranslations('repairs');
  const router = useRouter();

  const startMutation = useStartBatchMutation();
  const refuseMutation = useRefuseBatchMutation();
  const [view, setView] = useDataView('photo-scanning');

  const [dbSynced] = useState(true); // real impl polls status endpoint

  // Overlay open-state lives in the URL hash (#pdf/<batchId>, #refuse/<batchId>)
  // so overlays are deep-linkable and close on browser Back. Destructure the
  // stable callbacks so they can go in hook dep arrays without tripping the
  // React Compiler's manual-memoization check.
  const {
    open: pdfOpen,
    param: pdfIdParam,
    openWith: openPdf,
    onOpenChange: closePdf
  } = useHashParamDialog(PHOTO_SCANNING_HASH.pdf);
  const {
    open: refuseOpen,
    param: refuseId,
    openWith: openRefuse,
    onOpenChange: closeRefuse
  } = useHashParamDialog(PHOTO_SCANNING_HASH.refuse);

  // PDF viewer state — the iframe pulls the file from an authenticated API
  // route (so it works the same in local-dev and GCS prod). The open batch id
  // is derived from the hash param; `pdfTitle` stays local display data. The
  // hook below converts the id to an object URL we can hand to <iframe src>.
  const pdfBatchId = pdfOpen && pdfIdParam ? Number(pdfIdParam) : null;
  const [pdfTitle, setPdfTitle] = useState('');
  const pdfBlobUrl = useAuthenticatedBlobUrl(
    pdfBatchId ? API_ENDPOINTS.PHOTO_SCANNING.BATCH_PDF(pdfBatchId) : null
  );

  // URL state — read by queryParams; written by useDataTable filters + pagination
  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlContainerNumber] = useQueryState(
    'container_id',
    parseAsString.withDefault('')
  );
  const [urlStatusRaw] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlScanner] = useQueryState(
    'scanner',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlOperator] = useQueryState(
    'operator',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlRepairStatus] = useQueryState(
    'repair_status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlRepairCount] = useQueryState(
    'repair_count',
    parseAsString.withDefault('')
  );
  const [urlSort] = useQueryState('sort', parseAsString.withDefault(''));

  const queryParams = useMemo<ScanBatchListParams>(() => {
    const params: ScanBatchListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlContainerNumber) params.container_number = urlContainerNumber;
    if (urlStatusRaw.length > 0) {
      params.status = urlStatusRaw as BatchStatus[];
    }
    if (urlScanner.length > 0) {
      const ids = urlScanner.map(Number).filter((n) => n > 0);
      if (ids.length > 0) params.device_id = ids;
    }
    if (urlOperator.length > 0) {
      const ids = urlOperator.map(Number).filter((n) => n > 0);
      if (ids.length > 0) params.operator_id = ids;
    }
    if (urlRepairStatus.length > 0) {
      params.repair_status =
        urlRepairStatus as ScanBatchListParams['repair_status'];
    }
    if (urlRepairCount) {
      const parts = String(urlRepairCount)
        .split(',')
        .map(Number)
        .filter((n) => !isNaN(n));
      if (parts.length >= 1) params.min_repair_count = parts[0];
      if (parts.length >= 2) params.max_repair_count = parts[1];
    }
    if (urlSort) {
      const lastDot = urlSort.lastIndexOf('.');
      if (lastDot > 0) {
        params.sort_by = urlSort.slice(0, lastDot);
        params.sort_direction =
          urlSort.slice(lastDot + 1) === 'desc' ? 'desc' : 'asc';
      }
    }
    return params;
  }, [
    urlPage,
    urlPerPage,
    urlContainerNumber,
    urlStatusRaw,
    urlScanner,
    urlOperator,
    urlRepairStatus,
    urlRepairCount,
    urlSort
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    photoScanningQueries.list(queryParams)
  );

  const mapDeviceOption = useCallback(
    (d: Device) => ({
      value: String(d.id),
      label: d.serial_number ? `${d.serial_number} — ${d.name}` : d.name
    }),
    []
  );

  const mapUserOption = useCallback(
    (u: User) => ({ value: String(u.id), label: u.name }),
    []
  );

  const scannerInfinite = useInfiniteSelect<Device>({
    queryKey: deviceKeys.lists(),
    queryFn: (params) => devicesApi.getAll(params),
    mapOption: mapDeviceOption
  });

  const operatorInfinite = useInfiniteSelect<User>({
    queryKey: [...userKeys.lists(), { role: 'Operator' }],
    queryFn: (params) => usersApi.getAll(params),
    mapOption: mapUserOption,
    params: { role: 'Operator' }
  });

  const batches = useMemo(
    () => (data?.data ?? []) as ScanBatch[],
    [data?.data]
  );
  const pagination = data?.pagination;
  const pageCount = pagination?.totalPages ?? 0;

  // #refuse/<batchId> → resolve the hash id back to the loaded row (the batch
  // must be on the current page; deep-linking an off-page id won't open it).
  const refuseTarget = refuseOpen
    ? (batches.find((b) => String(b.id) === refuseId) ?? null)
    : null;

  async function handleStartScanning(batch: ScanBatch) {
    if (batch.status === 'in_progress') {
      // Already started — navigate directly, no API call needed
      router.push(`/dashboard/photo-scanning/${batch.id}`);
      return;
    }
    try {
      await startMutation.mutateAsync(batch.id);
      router.push(`/dashboard/photo-scanning/${batch.id}`);
    } catch {
      // handled by global MutationCache
    }
  }

  function handleViewPdf(batch: ScanBatch) {
    if (!batch.pdf_path) {
      toast.error(t('pdfNotReady'));
      return;
    }
    setPdfTitle(batch.batch_number);
    openPdf(String(batch.id));
  }

  function handleClosePdf() {
    closePdf(false);
    setPdfTitle('');
  }

  function handleConfirmRefuse(payload: MoveToRepairPayload) {
    if (refuseTarget?.registration_id == null) return;
    // This dialog never sets `optionalDestination`, so the destination is
    // always required and present here — narrow the shared payload type back
    // to non-optional for the mutation.
    if (payload.repair_destination_id == null) return;
    refuseMutation.mutate(
      {
        registrationId: refuseTarget.registration_id,
        ...payload,
        repair_destination_id: payload.repair_destination_id
      },
      { onSuccess: () => closeRefuse(false) }
    );
  }

  function isScanningDisabled(batch: ScanBatch) {
    if (batch.status === 'completed') return true;
    if (batch.status === 'in_progress') return false; // resume is always allowed
    return (
      batch.scanner_status !== 'online' || !dbSynced || startMutation.isPending
    );
  }

  function getScanDisabledReason(batch: ScanBatch): string | null {
    if (batch.status === 'completed') return null; // button hidden entirely
    if (batch.status === 'in_progress') return null;
    if (batch.scanner_status !== 'online') return t('scannerOfflineTooltip');
    if (!dbSynced) return t('dbNotSyncedTooltip');
    return null;
  }

  function getScanButtonLabel(batch: ScanBatch): string {
    if (batch.status === 'in_progress') return t('resumeScanning');
    return t('startScanning');
  }

  const statusOptions = useMemo(
    () => [
      { label: t('statusAssigned'), value: 'confirmed' },
      { label: t('statusInProgress'), value: 'in_progress' },
      { label: t('statusCompleted'), value: 'completed' }
    ],
    [t]
  );

  function getStatusLabel(status: BatchStatus): string {
    switch (status) {
      case 'confirmed':
        return t('statusAssigned');
      case 'in_progress':
        return t('statusInProgress');
      case 'completed':
        return t('statusCompleted');
      default:
        return t('statusPending');
    }
  }

  function getStatusVariant(
    status: BatchStatus
  ): 'default' | 'secondary' | 'outline' | 'destructive' {
    switch (status) {
      case 'confirmed':
        return 'secondary';
      case 'in_progress':
        return 'default';
      case 'completed':
        return 'outline';
      default:
        return 'secondary';
    }
  }

  const repairStatusOptions = useMemo(
    () => [
      { label: t('repairStatusNever'), value: 'never' },
      { label: tRepair('statusWaiting'), value: 'waiting' },
      { label: tRepair('statusCompleted'), value: 'completed' },
      { label: tRepair('statusReturned'), value: 'returned' }
    ],
    [t, tRepair]
  );

  function getRepairStatusLabel(status: RepairCycleStatus | null): string {
    switch (status) {
      case 'waiting':
        return tRepair('statusWaiting');
      case 'completed':
        return tRepair('statusCompleted');
      case 'returned':
        return tRepair('statusReturned');
      default:
        return t('repairStatusNever');
    }
  }

  const columns: ColumnDef<ScanBatch>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    // {
    //   id: 'box_id',
    //   accessorKey: 'box_id',
    //   header: ({ column }) => (
    //     <DataTableColumnHeader column={column} title={t('columnBoxId')} />
    //   ),
    //   cell: ({ row }) => (
    //     <span className='text-muted-foreground font-mono'>
    //       {row.original.box_id || '—'}
    //     </span>
    //   ),
    //   enableSorting: false,
    //   meta: {
    //     label: t('columnBoxId'),
    //     permission: 'photo_scanning.column.columnBoxId'
    //   }
    // },
    {
      id: 'container_id',
      accessorKey: 'container_id',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('columnContainerId')} />
      ),
      cell: ({ row, column }) => {
        const value = row.original.container_id;
        return (
          <span className='text-muted-foreground font-mono'>
            {value ? (
              <HighlightMatch
                text={String(value)}
                query={getColumnSearchTerms(column)}
              />
            ) : (
              '—'
            )}
          </span>
        );
      },
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('columnContainerId'),
        variant: 'text' as const,
        placeholder: t('searchPlaceholder'),
        permission: 'photo_scanning.column.columnContainerId'
      }
    },
    {
      id: 'scanner',
      accessorKey: 'scanner_id',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('columnScanner')} />
      ),
      cell: ({ row }) => (
        <span className='flex items-center gap-1.5 font-mono'>
          <span
            className={cn(
              'inline-block h-2 w-2 rounded-full',
              row.original.scanner_status === 'online'
                ? 'bg-emerald-500'
                : row.original.scanner_status === 'maintenance'
                  ? 'bg-amber-500'
                  : 'bg-rose-500'
            )}
          />
          {row.original.scanner_id} — {row.original.scanner_model}
        </span>
      ),
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('columnScanner'),
        variant: 'infiniteMultiSelect' as const,
        permission: 'photo_scanning.column.columnScanner'
      }
    },
    {
      id: 'operator',
      accessorFn: (row) => row.operator?.name ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('columnOperator')} />
      ),
      cell: ({ row }) => (
        <span className='text-muted-foreground'>
          {row.original.operator?.name ?? '—'}
        </span>
      ),
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('filterOperator'),
        variant: 'infiniteMultiSelect' as const,
        permission: 'photo_scanning.column.columnOperator'
      }
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('columnStatus')} />
      ),
      cell: ({ row }) => (
        <Badge variant={getStatusVariant(row.original.status)}>
          {getStatusLabel(row.original.status)}
        </Badge>
      ),
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('columnStatus'),
        variant: 'multiSelect' as const,
        options: statusOptions,
        permission: 'photo_scanning.column.columnStatus'
      }
    },
    {
      id: 'repair_status',
      accessorKey: 'repair_status',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('columnRepairStatus')}
        />
      ),
      cell: ({ row }) => {
        const status = row.original.repair_status;
        return (
          <Badge
            variant='secondary'
            className={cn(status ? REPAIR_STATUS_STYLES[status] : undefined)}
          >
            {getRepairStatusLabel(status)}
          </Badge>
        );
      },
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('columnRepairStatus'),
        variant: 'multiSelect' as const,
        options: repairStatusOptions,
        permission: 'photo_scanning.column.columnRepairStatus'
      }
    },
    {
      id: 'repair_count',
      accessorKey: 'repair_count',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('columnRepairCount')} />
      ),
      cell: ({ row }) => (
        <span className='text-muted-foreground'>
          {row.original.repair_count}
        </span>
      ),
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('columnRepairCount'),
        variant: 'range' as const,
        range: [0, 20] as [number, number],
        permission: 'photo_scanning.column.columnRepairCount'
      }
    },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('columnActions')}</div>,
      cell: ({ row }) => {
        const batch = row.original;
        const disabled = isScanningDisabled(batch);
        const disabledReason = getScanDisabledReason(batch);

        if (batch.status === 'completed') {
          return (
            <div className='flex items-center justify-end gap-1'>
              {batch.registration_id != null && (
                <RouteGate
                  route={`/dashboard/containers/${batch.registration_id}`}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size='icon'
                        variant='ghost'
                        onClick={() =>
                          router.push(
                            `/dashboard/containers/${batch.registration_id}`
                          )
                        }
                        className='h-8 w-8'
                      >
                        <Eye className='h-4 w-4' />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('viewContainer')}</TooltipContent>
                  </Tooltip>
                </RouteGate>
              )}
              <Button
                size='sm'
                variant='outline'
                onClick={() => handleViewPdf(batch)}
                className='gap-1.5'
              >
                <FileText className='h-3.5 w-3.5' />
                {t('viewPdf')}
              </Button>
              {batch.repair_count > 0 && (
                <RepairHistoryButton
                  batch={batch}
                  label={t('viewRepairHistory')}
                />
              )}
            </div>
          );
        }

        return (
          <div className='flex items-center justify-end gap-1'>
            {batch.registration_id != null && (
              <RouteGate
                route={`/dashboard/containers/${batch.registration_id}`}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size='icon'
                      variant='ghost'
                      onClick={() =>
                        router.push(
                          `/dashboard/containers/${batch.registration_id}`
                        )
                      }
                      className='h-8 w-8'
                    >
                      <Eye className='h-4 w-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('viewContainer')}</TooltipContent>
                </Tooltip>
              </RouteGate>
            )}
            {disabled && disabledReason ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-block'>
                    <Button
                      size='sm'
                      disabled
                      className='pointer-events-none opacity-50'
                    >
                      <ScanLine className='me-1.5 h-3.5 w-3.5' />
                      {getScanButtonLabel(batch)}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{disabledReason}</TooltipContent>
              </Tooltip>
            ) : (
              <Button
                size='sm'
                onClick={() => handleStartScanning(batch)}
                disabled={startMutation.isPending}
              >
                <ScanLine className='me-1.5 h-3.5 w-3.5' />
                {getScanButtonLabel(batch)}
              </Button>
            )}

            {/* Move to repair queue */}
            {batch.registration_id != null && (
              <PermissionGate permissions={['photo_scanning.edit']}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size='icon'
                      variant='ghost'
                      onClick={() => openRefuse(String(batch.id))}
                      className='h-8 w-8 text-amber-600 hover:text-amber-700'
                    >
                      <AlertTriangle className='h-4 w-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{tRepair('moveToRepair')}</TooltipContent>
                </Tooltip>
              </PermissionGate>
            )}

            {/* View repair history */}
            {batch.repair_count > 0 && (
              <RepairHistoryButton
                batch={batch}
                label={t('viewRepairHistory')}
              />
            )}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  const sortOptions = useMemo(
    () => [
      { label: t('sortBatchNumber'), value: 'batch_number' },
      { label: t('sortContainerId'), value: 'container_id' },
      { label: t('sortClientName'), value: 'client_name' },
      { label: t('sortScannedPages'), value: 'scanned_pages' },
      { label: t('sortCreatedAt'), value: 'created_at' }
    ],
    [t]
  );

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<ScanBatch>({
    columns: filteredColumns,
    pageKey: 'photo-scanning',
    data: batches,
    pageCount,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const tableState = table.state;

  return (
    <div className='space-y-4'>
      {/* Header row: controls + total counter */}
      <div className='flex items-center justify-end gap-4'>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='photo-scanning'
          />
        </div>
        {/* Total counter */}
        {/* <TotalMetricsCard /> */}
      </div>

      {/* Toolbar with column filters */}
      <DataTableToolbar
        table={table}
        viewFilterPermission='photo_scanning.view_filter'
        infiniteConfigs={{
          scanner: scannerInfinite,
          operator: operatorInfinite
        }}
      />

      {/* Overall progress: scanned / total across all batches */}
      {batches.length > 0 &&
        (() => {
          const totalPages = batches.reduce((s, b) => s + b.total_pages, 0);
          const scannedPages = batches.reduce((s, b) => s + b.scanned_pages, 0);
          const pct =
            totalPages > 0 ? Math.round((scannedPages / totalPages) * 100) : 0;
          return (
            <div className='bg-muted h-1 w-full overflow-hidden rounded-full'>
              <div
                className='h-full rounded-full bg-blue-500 transition-all'
                style={{ width: `${pct}%` }}
              />
            </div>
          );
        })()}

      {/* Data views */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : batches.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-16 text-center'>
          <AlertCircle className='text-muted-foreground mb-3 h-10 w-10' />
          <p className='text-muted-foreground'>{t('noAssignmentsFound')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {batches.map((batch) => (
              <PhotoScanningCard
                key={batch.id}
                batch={batch}
                onStartScanning={handleStartScanning}
                onViewPdf={handleViewPdf}
                isScanningDisabled={isScanningDisabled}
                getScanDisabledReason={getScanDisabledReason}
                getScanButtonLabel={getScanButtonLabel}
                isStartPending={startMutation.isPending}
                isPdfLoading={false}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {batches.map((batch) => (
              <PhotoScanningCompactRow
                key={batch.id}
                batch={batch}
                onStartScanning={handleStartScanning}
                onViewPdf={handleViewPdf}
                isScanningDisabled={isScanningDisabled}
                getScanDisabledReason={getScanDisabledReason}
                getScanButtonLabel={getScanButtonLabel}
                isStartPending={startMutation.isPending}
                isPdfLoading={false}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <DataTable
            table={table}
            actionBar={<PhotoScanningTableActionBar table={table} />}
          />
        </div>
      )}

      {/* Footer: system status */}
      <div className='flex items-center justify-between'>
        <SystemStatusBar />
      </div>

      {/* PDF Viewer Dialog */}
      <Dialog
        open={pdfBatchId !== null}
        onOpenChange={(open) => !open && handleClosePdf()}
      >
        <DialogContent className='flex h-[90vh] flex-col gap-0 p-0 sm:max-w-4xl'>
          <DialogHeader className='border-b px-6 py-4'>
            <DialogTitle className='flex items-center gap-2 font-mono'>
              <FileText className='h-4 w-4' />
              {t('pdfViewer')} — {pdfTitle}
            </DialogTitle>
          </DialogHeader>
          {pdfBatchId !== null && (
            <BatchPdfViewer
              key={pdfBatchId}
              batchId={pdfBatchId}
              pdfBlobUrl={pdfBlobUrl}
              title={pdfTitle}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Move to repair queue — #refuse/<batchId> */}
      <MoveToRepairDialog
        open={refuseOpen && !!refuseTarget}
        onOpenChange={closeRefuse}
        containerNumber={refuseTarget?.container_id}
        onConfirm={handleConfirmRefuse}
        isPending={refuseMutation.isPending}
      />
    </div>
  );
}
